import React, { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Tabs, Table, Tag, message, Segmented } from 'antd';
import type { TableColumnsType } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../graphql/types/SiteType';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { GET_WORK_PROGRESS_DATA, GetWorkProgressDataResponse, GetWorkProgressDataVariables } from '../graphql/queries/getWorkProgressData';
import {
  GET_WORK_PROGRESS_BAG_CUSTOMER_SUMMARY_PAGE,
  GetWorkProgressBagCustomerSummaryPageResponse,
  WorkProgressBagCustomerSummaryRow,
  WorkProgressBagSummaryAggregate,
} from '../graphql/queries/getWorkProgressBagCustomerSummaryPage';
import WorkProgressFilters from './components/WorkProgressFilters';
import ScanningChart from './components/ScanningChart';
import AccountScanningTable from './components/AccountScanningTable';
import BigBagProgressFilters, { BigBagProgressFiltersValue } from './components/BigBagProgressFilters';
import BigBagProgressDrilldownDrawer, { BigBagDrilldownContext } from './components/BigBagProgressDrilldownDrawer';
import { BIG_BAG_TYPE_NO_MAWB, BIG_BAG_TYPE_WITH_MAWB, type BigBagDrilldownBagType, type BigBagType } from './bagType';
import { BigBagDrilldownTitleSource } from './bigBagDrilldownTitleSource';
import {
  GET_WORK_PROGRESS_BAG_CUSTOMER_BREAKDOWN,
  GetWorkProgressBagCustomerBreakdownResponse,
  WorkProgressBagCustomerMawbSummaryRow,
  WorkProgressBagCustomerNoMawbSummaryRow,
} from '../graphql/queries/getWorkProgressBagCustomerBreakdown';
import {
  GET_WORK_PROGRESS_BAG_DIFF_CUSTOMER_SUMMARY_PAGE,
  GetWorkProgressBagDiffCustomerSummaryPageResponse,
  WorkProgressBagDiffCustomerSummaryRow,
} from '../graphql/queries/getWorkProgressBagDiffCustomerSummaryPage';
import {
  GET_WORK_PROGRESS_BAG_DIFF_CUSTOMER_BREAKDOWN,
  GetWorkProgressBagDiffCustomerBreakdownResponse,
  WorkProgressBagDiffCustomerMawbSummaryRow,
} from '../graphql/queries/getWorkProgressBagDiffCustomerBreakdown';
import {
  GET_WORK_PROGRESS_BAG_DIFF_MAWB_BAG_SUMMARIES,
  GetWorkProgressBagDiffMawbBagSummariesResponse,
  WorkProgressBagDiffMawbBagSummaryRow,
} from '../graphql/queries/getWorkProgressBagDiffMawbBagSummaries';
import {
  GET_WORK_PROGRESS_BAG_MAWB_BAG_PARCEL_SUMMARIES,
  GetWorkProgressBagMawbBagParcelSummariesResponse
} from 'pages/wms/progress/graphql/queries/getWorkProgressBagMawbBagParcelSummaries';
import {
  GENERATE_WORK_PROGRESS_BAG_DETAIL_DOWNLOAD_REPORT,
  GenerateWorkProgressBagDetailDownloadReportResponse,
} from '../graphql/mutations/generateWorkProgressBagDetailDownloadReport';
import {
  GENERATE_WORK_PROGRESS_BAG_DIFF_MAWB_DOWNLOAD_REPORT,
  GenerateWorkProgressBagDiffMawbDownloadReportResponse,
} from '../graphql/mutations/generateWorkProgressBagDiffMawbDownloadReport';
import {
  BigBagParcelDiffRateCell,
  CLICKABLE_UNDERLINE,
  CLIENT_CARTON_DATE_WINDOW_PAD_DAYS,
  mergeWithMawbRowsByMawb,
} from './bigBagHelpers';
import MawbBagDiffSummaryTable from './components/MawbBagDiffSummaryTable';
import MawbBagParcelSummaryTable from './components/MawbBagParcelSummaryTable';
import { useMawbExpandCache } from './useMawbExpandCache';

import WorkProgressBagDownloadModal from './components/WorkProgressBagDownloadModal';

// 扩展 dayjs 支持 UTC 功能
dayjs.extend(utc);

type WorkProgressTabKey = 'all' | 'big-bag';
/** 「大包进度」下的子 tab，取代旧的「是否看大包预实差」开关 */
type BigBagSubTabKey = 'parcel' | 'bag';

/** 大包页「明细下钻」提示天数，与分拣数据看板下钻说明一致 */
const BIG_BAG_DRILLDOWN_HINT_DAYS = 30;

/** 一级客户汇总表（含预期/入库大包两列）列宽 */
const BIG_BAG_CUSTOMER_ALIGNED_COL_PX = [200, 120, 120, 120, 120, 120, 220, 100] as const;
/** 二级「有提单号」原生表列宽（与主表一致：客户/预期大包/入库大包/预期包裹/入库包裹/差异/差异包裹率/状态） */
const BIG_BAG_MAWB_ALIGNED_COL_PX = BIG_BAG_CUSTOMER_ALIGNED_COL_PX;
/** 与一级 Ant Table 展开列同宽，用于原生二级表 colgroup 首列 */
const BIG_BAG_NATIVE_EXPAND_COL_PX = 48;
/** 已入库明细查 client_carton 不能去掉时间范围，这里放宽前后两天避免扫描时间跨 UTC 日导致查不到。 */
const formatClientCartonQueryDateRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => ({
  start: start.startOf('day').subtract(CLIENT_CARTON_DATE_WINDOW_PAD_DAYS, 'day').format('YYYY-MM-DD'),
  end: end.startOf('day').add(CLIENT_CARTON_DATE_WINDOW_PAD_DAYS, 'day').format('YYYY-MM-DD'),
});

const WorkProgressPage: React.FC = () => {
  const { currentWarehouse } = useWarehouseContext();
  const { t } = useTranslation();

  const [activeTabKey, setActiveTabKey] = useState<WorkProgressTabKey>('all');

  // 时间范围状态 - 默认24小时
  const [workProgressTimeRange, setWorkProgressTimeRange] = useState<{ start: dayjs.Dayjs; end: dayjs.Dayjs }>(() => {
    const end = dayjs().minute(0).second(0).millisecond(0);
    const start = end.subtract(24, 'hour').minute(0).second(0).millisecond(0);
    return { start, end };
  });

  // 筛选条件状态
  const [channelId, setChannelId] = useState<string | null>(null);
  const [carrierCode, setCarrierCode] = useState<number | null>(null);

  // 「大包进度」下的子 tab；切换时通过 key 强制 BigBagProgressFilters 重新挂载、筛选与分页重置
  const [bigBagSubTabKey, setBigBagSubTabKey] = useState<BigBagSubTabKey>('parcel');

  // 大包进度独立筛选条件
  const [bigBagFilters, setBigBagFilters] = useState<BigBagProgressFiltersValue | null>(null);
  const [bigBagFilterOptions, setBigBagFilterOptions] = useState<{
    customers: { customerId: number; customerName: string }[];
  }>({
    customers: [],
  });
  const [bigBagPage, setBigBagPage] = useState<{ page: number; pageSize: number }>({ page: 1, pageSize: 10 });
  const [bigBagDrilldown, setBigBagDrilldown] = useState<{
    open: boolean;
    context: BigBagDrilldownContext | null;
    mode: 'bag' | 'parcel';
    isInbound: boolean | null;
    titleSource: BigBagDrilldownTitleSource | null;
  }>({
    open: false,
    context: null,
    mode: 'bag',
    isInbound: null,
    titleSource: null,
  });
  const [expandedCustomerKeys, setExpandedCustomerKeys] = useState<React.Key[]>([]);
  /**
   * 客户展开缓存。两个子 tab 的 breakdown 形状不同：
   * - 包裹差异：包含 `withMawb`（含包裹字段）与 `noMawb`；
   * - 大包差异：仅含 `withMawb`（只有大包字段，且 `noMawb` 不存在）。
   * 通过 `bigBagSubTabKey` 配合渲染时收窄类型。
   */
  type BigBagBreakdownData =
    | GetWorkProgressBagCustomerBreakdownResponse['getWorkProgressBagCustomerBreakdown']
    | GetWorkProgressBagDiffCustomerBreakdownResponse['getWorkProgressBagDiffCustomerBreakdown'];
  const [customerBreakdownById, setCustomerBreakdownById] = useState<Record<number, BigBagBreakdownData | null>>(
    {}
  );
  // 包裹差异 / 大包差异 各持一份 MAWB 行展开缓存，子 tab 切换时整体 clearAll。
  // 详见下方 `parcelDiffMawbCache` / `bagDiffMawbCache`（fetcher 在 useLazyQuery 之后初始化）。
  const bigBagTableAnchorRef = useRef<HTMLDivElement | null>(null);

  const scrollToBigBagTable = () => {
    bigBagTableAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const canOpenBigBagDrilldown = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const maxLookbackStart = dayjs().startOf('day').subtract(BIG_BAG_DRILLDOWN_HINT_DAYS, 'day');
    const today = dayjs().startOf('day');
    const s = start.startOf('day');
    const e = end.startOf('day');
    if (s.isBefore(maxLookbackStart) || e.isAfter(today)) {
      message.warning(t('wms.progress.bigBag.drilldownHint', { days: BIG_BAG_DRILLDOWN_HINT_DAYS }));
      return false;
    }
    return true;
  };

  // 获取 hubId：如果当前是 HUB，使用当前 id；如果是 WAREHOUSE，使用 parentSiteId
  const hubId = useMemo(() => {
    if (!currentWarehouse) return null;
    if (currentWarehouse.siteType === SiteType.HUB) {
      return currentWarehouse.id;
    } else if (currentWarehouse.siteType === SiteType.WAREHOUSE && currentWarehouse.parentSiteId) {
      return String(currentWarehouse.parentSiteId);
    }
    return null;
  }, [currentWarehouse]);

  // 全量进度 GraphQL 查询
  const { data, loading } = useQuery<GetWorkProgressDataResponse, GetWorkProgressDataVariables>(
    GET_WORK_PROGRESS_DATA,
    {
      variables: {
        hubId: hubId || '',
        startTime: workProgressTimeRange.start.minute(0).second(0).millisecond(0).utc().format('YYYY-MM-DD HH:mm'),
        endTime: workProgressTimeRange.end.minute(0).second(0).millisecond(0).utc().format('YYYY-MM-DD HH:mm'),
        channelId: channelId || null,
        carrierCode: carrierCode ?? null,
      },
      skip: !hubId || activeTabKey !== 'all',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  // 大包进度 - 包裹差异子 tab：按客户汇总分页（原查询，showBagExpectedDiff=false）
  const { data: bigBagParcelDiffData, loading: bigBagParcelDiffLoading } = useQuery<GetWorkProgressBagCustomerSummaryPageResponse>(
    GET_WORK_PROGRESS_BAG_CUSTOMER_SUMMARY_PAGE,
    {
      variables: bigBagFilters && hubId
        ? {
            filter: {
              hubId: Number(hubId),
              arrivalDateStart: bigBagFilters.timeRange.start.format('YYYY-MM-DD'),
              arrivalDateEnd: bigBagFilters.timeRange.end.format('YYYY-MM-DD'),
              customerId: bigBagFilters.customerId,
              showBagExpectedDiff: false,
              page: bigBagPage.page,
              pageSize: bigBagPage.pageSize,
              sortField: null,
              sortDirection: null,
            },
          }
        : undefined,
      skip:
        !hubId ||
        !bigBagFilters ||
        activeTabKey !== 'big-bag' ||
        bigBagSubTabKey !== 'parcel',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  // 大包进度 - 大包差异子 tab：按应揽收仓查新表 work_progress_bag_diff_summary
  const { data: bigBagBagDiffData, loading: bigBagBagDiffLoading } = useQuery<GetWorkProgressBagDiffCustomerSummaryPageResponse>(
    GET_WORK_PROGRESS_BAG_DIFF_CUSTOMER_SUMMARY_PAGE,
    {
      variables: bigBagFilters && hubId
        ? {
            filter: {
              prePickupHubId: Number(hubId),
              arrivalDateStart: bigBagFilters.timeRange.start.format('YYYY-MM-DD'),
              arrivalDateEnd: bigBagFilters.timeRange.end.format('YYYY-MM-DD'),
              customerId: bigBagFilters.customerId,
              page: bigBagPage.page,
              pageSize: bigBagPage.pageSize,
              sortField: null,
              sortDirection: null,
            },
          }
        : undefined,
      skip:
        !hubId ||
        !bigBagFilters ||
        activeTabKey !== 'big-bag' ||
        bigBagSubTabKey !== 'bag',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  /** 子 tab 维度统一向后续逻辑暴露同名「Summary Page」字段，下游渲染无需关心数据来源。 */
  const bigBagCustomerSummaryPage = bigBagSubTabKey === 'bag'
    ? bigBagBagDiffData?.getWorkProgressBagDiffCustomerSummaryPage
    : bigBagParcelDiffData?.getWorkProgressBagCustomerSummaryPage;
  const bigBagLoading = bigBagSubTabKey === 'bag' ? bigBagBagDiffLoading : bigBagParcelDiffLoading;

  const [fetchCustomerBreakdownParcelDiff, { loading: customerBreakdownParcelLoading }] =
    useLazyQuery<GetWorkProgressBagCustomerBreakdownResponse>(GET_WORK_PROGRESS_BAG_CUSTOMER_BREAKDOWN, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });
  const [fetchCustomerBreakdownBagDiff, { loading: customerBreakdownBagLoading }] =
    useLazyQuery<GetWorkProgressBagDiffCustomerBreakdownResponse>(GET_WORK_PROGRESS_BAG_DIFF_CUSTOMER_BREAKDOWN, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });
  const customerBreakdownLoading = bigBagSubTabKey === 'bag' ? customerBreakdownBagLoading : customerBreakdownParcelLoading;

  const [fetchMawbBagParcelSummaries] =
    useLazyQuery<GetWorkProgressBagMawbBagParcelSummariesResponse>(GET_WORK_PROGRESS_BAG_MAWB_BAG_PARCEL_SUMMARIES, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });
  const [fetchMawbBagDiffSummaries] =
    useLazyQuery<GetWorkProgressBagDiffMawbBagSummariesResponse>(GET_WORK_PROGRESS_BAG_DIFF_MAWB_BAG_SUMMARIES, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });

  // 子 tab 三级表展开缓存：两个 tab 各持一份，子 tab 切换时整体 clearAll；
  // fetcher 闭包捕获当前 `hubId` / `bigBagFilters`，依赖变化时 React 会重建 toggle 引用。
  const parcelDiffMawbCache = useMawbExpandCache<
    GetWorkProgressBagMawbBagParcelSummariesResponse['getWorkProgressBagMawbBagParcelSummaries'][number]
  >(async (customerId, mawb) => {
    if (!hubId || !bigBagFilters) return [];
    const isBagDiffTab = bigBagSubTabKey === 'bag';
    const rangeStart = bigBagFilters.timeRange.start.startOf('day').format('YYYY-MM-DD');
    const rangeEnd = bigBagFilters.timeRange.end.startOf('day').format('YYYY-MM-DD');
    const res = await fetchMawbBagParcelSummaries({
      variables: {
        filter: {
          hubId: Number(hubId),
          arrivalDateStart: rangeStart,
          arrivalDateEnd: rangeEnd,
          customerId,
          mawb,
          inboundBagOnly: !isBagDiffTab,
          ...(isBagDiffTab ? {} : { isInbound: true }),
        },
      },
    });
    return res?.data?.getWorkProgressBagMawbBagParcelSummaries ?? [];
  });

  const bagDiffMawbCache = useMawbExpandCache<WorkProgressBagDiffMawbBagSummaryRow>(async (customerId, mawb) => {
    if (!hubId || !bigBagFilters) return [];
    const rangeStart = bigBagFilters.timeRange.start.startOf('day').format('YYYY-MM-DD');
    const rangeEnd = bigBagFilters.timeRange.end.startOf('day').format('YYYY-MM-DD');
    const res = await fetchMawbBagDiffSummaries({
      variables: {
        filter: {
          prePickupHubId: Number(hubId),
          arrivalDateStart: rangeStart,
          arrivalDateEnd: rangeEnd,
          customerId,
          mawb,
        },
      },
    });
    return res?.data?.getWorkProgressBagDiffMawbBagSummaries ?? [];
  });

  // 两个子 tab 的「下载数据」mutation
  const [triggerBagDetailDownload, { loading: bagDetailDownloadLoading }] =
    useMutation<GenerateWorkProgressBagDetailDownloadReportResponse>(GENERATE_WORK_PROGRESS_BAG_DETAIL_DOWNLOAD_REPORT);
  const [triggerBagDiffMawbDownload, { loading: bagDiffMawbDownloadLoading }] =
    useMutation<GenerateWorkProgressBagDiffMawbDownloadReportResponse>(GENERATE_WORK_PROGRESS_BAG_DIFF_MAWB_DOWNLOAD_REPORT);

  // 下载弹窗（与下钻 drawer 的导出体验保持一致：toast → 弹窗 + 文件名 + 前往下载页）
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');

  const handleBigBagDownload = async () => {
    if (!hubId || !bigBagFilters) {
      message.warning(t('wms.progress.bigBag.messages.noFiltersForDownload'));
      return;
    }
    const start = bigBagFilters.timeRange.start.format('YYYY-MM-DD');
    const end = bigBagFilters.timeRange.end.format('YYYY-MM-DD');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const fileName = bigBagSubTabKey === 'bag'
        ? (await triggerBagDiffMawbDownload({
            variables: {
              filter: {
                prePickupHubId: Number(hubId),
                arrivalDateStart: start,
                arrivalDateEnd: end,
                customerId: bigBagFilters.customerId,
              },
              timezone,
            },
          })).data?.generateWorkProgressBagDiffMawbDownloadReport
        : (await triggerBagDetailDownload({
            variables: {
              filter: {
                hubId: Number(hubId),
                arrivalDateStart: start,
                arrivalDateEnd: end,
                bagType: 1,
                customerId: bigBagFilters.customerId,
                mawb: null,
              },
              timezone,
            },
          })).data?.generateWorkProgressBagDetailDownloadReport;
      if (fileName) {
        setDownloadFileName(fileName);
        setDownloadModalVisible(true);
      } else {
        message.error(t('wms.progress.bigBag.messages.downloadFailed'));
      }
    } catch (e) {
      message.error(t('wms.progress.bigBag.messages.downloadFailed'));
    }
  };

  // 获取statTypes（全量进度）
  const statTypes = useMemo(() => data?.getWorkProgressData?.statTypes || [], [data]);

  // 转换数据为表格格式（按 accountId 分组，累加各类型数据）
  // 使用legendKey作为字段名
  const accountScanningData = useMemo(() => {
    if (!data?.getWorkProgressData?.accountSummaries || statTypes.length === 0) {
      return [];
    }
    return data.getWorkProgressData.accountSummaries.map(summary => {
      const row: any = {
        accountId: summary.accountId,
        account: summary.accountName,
        accountPhone: summary.accountPhone,
      };
      // 根据statTypes动态添加字段
      statTypes.forEach(statType => {
        switch (statType.legendKey) {
          case 'ArrivalScan':
            row[statType.legendKey] = Number(summary.pickupArrival);
            break;
          case 'SortingScan':
            row[statType.legendKey] = Number(summary.sorting);
            break;
          case 'BaggingScan':
            row[statType.legendKey] = Number(summary.bagging);
            break;
          case 'GenerateBaggingTask':
            row[statType.legendKey] = Number(summary.baggingTask);
            break;
          case 'DepartureScan':
            row[statType.legendKey] = Number(summary.departure);
            break;
        }
      });
      return row;
    });
  }, [data, statTypes]);

  // 转换数据为折线图格式（按 hour + 类型分组，累加所有 account 的数据）
  // 将服务端返回的UTC时间转换为前端本地时区
  // 使用legendKey作为字段名
  const scanningData = useMemo(() => {
    if (!data?.getWorkProgressData?.hourlyData || statTypes.length === 0) {
      return [];
    }
    return data.getWorkProgressData.hourlyData.map(point => {
      // 服务端返回的 hour 是 UTC+0 时区的格式 "YYYY-MM-DD HH:mm"
      // 需要先解析为 UTC 时间，然后转换为本地时区
      let hourDisplay = point.hour;
      try {
        const utcTime = dayjs.utc(point.hour, 'YYYY-MM-DD HH:mm');
        if (utcTime.isValid()) {
          const localTime = utcTime.local();
          hourDisplay = localTime.format('YYYY-MM-DD HH:mm');
        }
      } catch (e) {
        console.warn('Failed to parse hour:', point.hour, e);
      }
      const row: any = {
        hour: hourDisplay,
      };
      // 根据statTypes动态添加字段
      statTypes.forEach(statType => {
        switch (statType.legendKey) {
          case 'ArrivalScan':
            row[statType.legendKey] = Number(point.pickupArrival);
            break;
          case 'SortingScan':
            row[statType.legendKey] = Number(point.sorting);
            break;
          case 'BaggingScan':
            row[statType.legendKey] = Number(point.bagging);
            break;
          case 'GenerateBaggingTask':
            row[statType.legendKey] = Number(point.baggingTask);
            break;
          case 'DepartureScan':
            row[statType.legendKey] = Number(point.departure);
            break;
        }
      });
      return row;
    });
  }, [data, statTypes]);

  useEffect(() => {
    const allCustomers = bigBagCustomerSummaryPage?.allCustomers ?? [];
    setBigBagFilterOptions({ customers: allCustomers });
  }, [bigBagCustomerSummaryPage]);

  const bigBagTotal = bigBagCustomerSummaryPage?.totalElements ?? 0;
  /**
   * 两个 tab 的 aggregate 形状不同：包裹差异有 parcel 字段、大包差异只有 bag 字段。
   * 渲染层用 `?? 0` 兜底取值，类型用宽松的 `Partial<WorkProgressBagSummaryAggregate>` 表达。
   */
  const bigBagAggregate: Partial<WorkProgressBagSummaryAggregate> | null =
    bigBagCustomerSummaryPage?.aggregate ?? null;
  const bigBagDataCalculatedAtMaxIso =
    bigBagCustomerSummaryPage?.dataCalculatedAtMax ?? null;
  /** 与包裹 SLA 监控页（useSlaMonitoring / ParcelSlaMonitoringPage）一致：UTC → 浏览器本地，YYYY-MM-DD HH:mm:ss */
  const bigBagDataUpdatedAtText = useMemo(() => {
    const calculatedAt = bigBagDataCalculatedAtMaxIso;
    if (!calculatedAt) return '';
    try {
      const d = dayjs.utc(calculatedAt);
      return d.isValid() ? d.local().format('YYYY-MM-DD HH:mm:ss') : String(calculatedAt);
    } catch {
      return String(calculatedAt);
    }
  }, [bigBagDataCalculatedAtMaxIso]);
  /** 由子 tab 派生：「大包差异」tab 切换两套表头与查询。 */
  const showBagExpectedDiff = bigBagSubTabKey === 'bag';
  const bigBagNativeAlignedColPx = useMemo(
    // 大包差异 tab 二级列：客户名(200) / 预期大包(120) / 入库大包(120) / 大包差异(120) / 大包差异率(220) / 状态(100)
    // 包裹差异 tab 二级列：客户名(200) / 入库大包(120) / 预期包裹(120) / 入库包裹(120) / 差异包裹(120) / 差异包裹率(220) / 状态(100)
    () => showBagExpectedDiff
      ? [200, 120, 120, 120, 220, 100]
      : BIG_BAG_MAWB_ALIGNED_COL_PX.filter((_, index) => index !== 1),
    [showBagExpectedDiff]
  );
  const bigBagNativeTableMinWidth = BIG_BAG_NATIVE_EXPAND_COL_PX + bigBagNativeAlignedColPx.reduce((sum, width) => sum + width, 0);
  const bigBagExpandedColSpan = 1 + bigBagNativeAlignedColPx.length;

  /**
   * 抽出来的 drawer context 构造器：三个 open*Drilldown 入口的公共部分。
   * - `hubId` / `inboundBagOnly` / `isInbound` 三个字段口径一致；
   * - `prePickupHubId` 默认按"大包差异 tab 才传"，KPI 卡的 parcel 模式调用方显式 override 为 null；
   * - `clientCartonDateRange` 仅 openBagDetailDrilldown（已入库走 client_carton）会传。
   */
  const buildDrilldownContext = (params: {
    dateRange: { start: dayjs.Dayjs; end: dayjs.Dayjs };
    clientCartonDateRange?: { start: string; end: string } | null;
    customerId: number | null;
    customerName: string | null;
    mawb?: string | null;
    bigBagNo?: string | null;
    bagType: BigBagDrilldownContext['bagType'];
    /** 不传 = 跟随 `showBagExpectedDiff`；显式传 null = 强制不下发（KPI parcel 模式用）。 */
    prePickupHubIdOverride?: number | null;
  }): BigBagDrilldownContext => ({
    hubId: Number(hubId),
    prePickupHubId:
      params.prePickupHubIdOverride !== undefined
        ? params.prePickupHubIdOverride
        : (showBagExpectedDiff ? Number(hubId) : null),
    arrivalDateStart: params.dateRange.start.format('YYYY-MM-DD'),
    arrivalDateEnd: params.dateRange.end.format('YYYY-MM-DD'),
    clientCartonArrivalDateStart: params.clientCartonDateRange?.start ?? null,
    clientCartonArrivalDateEnd: params.clientCartonDateRange?.end ?? null,
    customerId: params.customerId,
    customerName: params.customerName,
    mawb: params.mawb ?? null,
    bigBagNo: params.bigBagNo ?? null,
    bagType: params.bagType,
    isInbound: null,
    inboundBagOnly: !showBagExpectedDiff,
  });

  const openBigBagKpiDrilldown = (mode: 'parcel' | 'bag', nextIsInbound: boolean | null) => {
    if (!bigBagFilters || !hubId) return;
    const start = bigBagFilters.timeRange.start.startOf('day');
    const end = bigBagFilters.timeRange.end.startOf('day');
    if (!canOpenBigBagDrilldown(start, end)) return;
    const selectedCustomer = bigBagFilters.customerId != null
      ? (bigBagFilterOptions.customers ?? []).find(c => c.customerId === bigBagFilters.customerId)
      : undefined;

    setBigBagDrilldown({
      open: true,
      context: buildDrilldownContext({
        dateRange: { start, end },
        customerId: bigBagFilters.customerId ?? null,
        customerName: selectedCustomer?.customerName ?? null,
        bagType: mode === 'parcel' ? null : BIG_BAG_TYPE_WITH_MAWB,
        // KPI 卡只有 bag 模式（大包差异 tab）传 prePickupHubId；parcel 模式无论哪个 tab 都不传。
        prePickupHubIdOverride: mode === 'bag' && showBagExpectedDiff ? Number(hubId) : null,
      }),
      mode,
      isInbound: nextIsInbound,
      titleSource:
        mode === 'bag'
          ? (nextIsInbound === true
              ? BigBagDrilldownTitleSource.KpiBagInbound
              : BigBagDrilldownTitleSource.KpiBagDiffNotInbound)
          : BigBagDrilldownTitleSource.KpiParcelDiffNotInbound,
    });
  };

  const openParcelDrilldown = (
    ctx: {
      customerId: number;
      customerName: string;
      bagType: BigBagDrilldownBagType;
      /** 仅 bagType=有提单号：提单号 MAWB（写入 drilldown context.mawb / GraphQL mawb） */
      mawb?: string | null;
      /** 无提单号：大包号；有提单号：可选，用于按大包收窄包裹明细（写入 drilldown bigBagNo / GraphQL bagNumber） */
      bagNo?: string | null;
    },
    titleSource: BigBagDrilldownTitleSource,
  ) => {
    if (!hubId || !bigBagFilters) return;
    const start = bigBagFilters.timeRange.start.startOf('day');
    const end = bigBagFilters.timeRange.end.startOf('day');
    if (!canOpenBigBagDrilldown(start, end)) return;
    const mawbTrimmed = ctx.bagType === BIG_BAG_TYPE_WITH_MAWB ? (ctx.mawb?.trim() ?? '') : '';
    const bagNoTrimmed = ctx.bagNo?.trim() ?? '';
    setBigBagDrilldown({
      open: true,
      context: buildDrilldownContext({
        dateRange: { start, end },
        customerId: ctx.customerId,
        customerName: ctx.customerName,
        mawb: mawbTrimmed || null,
        bigBagNo: bagNoTrimmed || null,
        bagType: ctx.bagType,
        // 包裹差异下钻 mode=parcel，drawer 用 getWorkProgressBagParcelDetails 不读 prePickupHubId；显式 null 表意。
        prePickupHubIdOverride: null,
      }),
      mode: 'parcel',
      isInbound: null,
      titleSource,
    });
  };

  const openBagDetailDrilldown = (
    ctx: {
      customerId: number;
      customerName: string;
      bagType: BigBagType;
      mawb?: string | null;
      bigBagNo?: string | null;
      /** 列表行上的到港日：有值时抽屉按该日查询，标题与行内日期一致（否则用筛选栏整段区间） */
      detailArrivalDate?: string | null;
    },
    opts: {
      titleSource: BigBagDrilldownTitleSource;
      /** 已入库大包需要传 true 走 client_carton；未入库/未指定不传 isInbound */
      nextIsInbound?: true | null;
    },
  ) => {
    const { titleSource, nextIsInbound = null } = opts;
    if (!hubId || !bigBagFilters) return;
    const filterStart = bigBagFilters.timeRange.start.startOf('day');
    const filterEnd = bigBagFilters.timeRange.end.startOf('day');
    if (!canOpenBigBagDrilldown(filterStart, filterEnd)) return;

    const rawDay = ctx.detailArrivalDate?.trim() ?? '';
    const detailDay = rawDay && dayjs(rawDay, 'YYYY-MM-DD', true).isValid() ? dayjs(rawDay).startOf('day') : null;
    const rangeStart = detailDay ?? filterStart;
    const rangeEnd = detailDay ?? filterEnd;
    const clientCartonQueryRange = nextIsInbound === true
      ? formatClientCartonQueryDateRange(rangeStart, rangeEnd)
      : null;

    setBigBagDrilldown({
      open: true,
      context: buildDrilldownContext({
        dateRange: { start: rangeStart, end: rangeEnd },
        clientCartonDateRange: clientCartonQueryRange,
        customerId: ctx.customerId,
        customerName: ctx.customerName,
        mawb: ctx.mawb ?? null,
        bigBagNo: ctx.bigBagNo ?? null,
        bagType: ctx.bagType,
        // 默认（不 override）：大包差异 tab 传 prePickupHubId、包裹差异 tab 不传。
      }),
      mode: 'bag',
      isInbound: nextIsInbound,
      titleSource,
    });
  };

  // 离开大包 Tab：折叠全部展开，避免切回「全量进度」再回来时加号仍为展开
  useEffect(() => {
    if (activeTabKey === 'big-bag') return;
    setExpandedCustomerKeys([]);
    setCustomerBreakdownById({});
    parcelDiffMawbCache.clearAll();
    bagDiffMawbCache.clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabKey]);

  // 筛选条件变化时重置到第一页，并折叠全部展开（下钻抽屉不改变筛选 deps，故不影响展开）
  useEffect(() => {
    setExpandedCustomerKeys([]);
    setCustomerBreakdownById({});
    parcelDiffMawbCache.clearAll();
    bagDiffMawbCache.clearAll();
    if (bigBagFilters) {
      setBigBagPage(prev => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bigBagFilters?.customerId, bigBagFilters?.timeRange?.start, bigBagFilters?.timeRange?.end, bigBagSubTabKey]);

  type CustomerSummaryRow = {
    key: string;
    customerId: number;
    customerName: string;
    /** 与主表接口一致：二级将仅有「无提单号」时，预期大包数列展示「-」 */
    onlyNoMawbBreakdownRows: boolean;
    expectedBagCount: number;
    inboundBagCount: number;
    bagDiff: number;
    bagDiffRate: number;
    /** 仅包裹差异 tab 使用；大包差异 tab 下查询不返回，全部填 0 */
    expectedParcelCount: number;
    inboundParcelCount: number;
    parcelDiff: number;
    parcelDiffRate: number;
    status: 'high' | 'mid' | 'normal';
  };

  const customerSummaryRows: CustomerSummaryRow[] = useMemo(() => {
    /** 两种 tab 的内容行结构差异：bag tab 只返回大包字段；统一展平后再按 tab 决定状态口径。 */
    type AnySummaryRow =
      | WorkProgressBagCustomerSummaryRow
      | WorkProgressBagDiffCustomerSummaryRow;
    const list: AnySummaryRow[] = bigBagCustomerSummaryPage?.content ?? [];
    const useBagDiff = bigBagSubTabKey === 'bag';
    const rowsByCustomerId = new Map<number, CustomerSummaryRow>();
    list.forEach(rawRow => {
      const r = rawRow as Partial<WorkProgressBagCustomerSummaryRow> & WorkProgressBagDiffCustomerSummaryRow;
      const existing = rowsByCustomerId.get(r.customerId);
      const nextExpectedBagCount = Number(r.expectedBagCountSum ?? 0);
      const nextInboundBagCount = Number(r.inboundBagCountSum ?? 0);
      const nextBagDiff = Number(r.bagDiffSum ?? 0);
      const nextExpectedParcelCount = Number(r.expectedParcelCountSum ?? 0);
      const nextInboundParcelCount = Number(r.inboundParcelCountSum ?? 0);
      const nextParcelDiff = Number(r.parcelDiffSum ?? 0);
      if (existing) {
        existing.expectedBagCount += nextExpectedBagCount;
        existing.inboundBagCount += nextInboundBagCount;
        existing.bagDiff += nextBagDiff;
        existing.bagDiffRate = existing.expectedBagCount > 0 ? existing.bagDiff / existing.expectedBagCount : 0;
        existing.expectedParcelCount += nextExpectedParcelCount;
        existing.inboundParcelCount += nextInboundParcelCount;
        existing.parcelDiff += nextParcelDiff;
        existing.parcelDiffRate = existing.expectedParcelCount > 0 ? existing.parcelDiff / existing.expectedParcelCount : 0;
        existing.onlyNoMawbBreakdownRows = existing.onlyNoMawbBreakdownRows && Boolean(r.onlyNoMawbBreakdownRows);
        const activeRate = useBagDiff ? existing.bagDiffRate : existing.parcelDiffRate;
        existing.status = activeRate > 0.1 ? 'high' : activeRate > 0 ? 'mid' : 'normal';
        return;
      }
      const bagRate = Number(r.bagDiffRate ?? 0);
      const parcelRate = Number(r.parcelDiffRate ?? 0);
      const activeRate = useBagDiff ? bagRate : parcelRate;
      const status: CustomerSummaryRow['status'] = activeRate > 0.1 ? 'high' : activeRate > 0 ? 'mid' : 'normal';
      rowsByCustomerId.set(r.customerId, {
        key: String(r.customerId),
        customerId: r.customerId,
        customerName: r.customerName,
        onlyNoMawbBreakdownRows: Boolean(r.onlyNoMawbBreakdownRows),
        expectedBagCount: nextExpectedBagCount,
        inboundBagCount: nextInboundBagCount,
        bagDiff: nextBagDiff,
        bagDiffRate: bagRate,
        expectedParcelCount: nextExpectedParcelCount,
        inboundParcelCount: nextInboundParcelCount,
        parcelDiff: nextParcelDiff,
        parcelDiffRate: parcelRate,
        status,
      });
    });
    return Array.from(rowsByCustomerId.values());
  }, [bigBagCustomerSummaryPage, bigBagSubTabKey]);

  const customerColumns: TableColumnsType<CustomerSummaryRow> = useMemo(() => {
    const customerNameCol = {
      title: t('wms.progress.bigBag.columns.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 200,
      fixed: 'left' as const,
    };
    const expectedBagCountCol = {
      title: t('wms.progress.bigBag.columns.expectedBagCount'),
      dataIndex: 'expectedBagCount',
      key: 'expectedBagCount',
      width: 120,
      render: (v: number, record: CustomerSummaryRow) =>
        record.onlyNoMawbBreakdownRows ? (
          <span className="tabular-nums text-gray-700">-</span>
        ) : Number(v ?? 0) > 0 ? (
          <span className="tabular-nums">{Number(v ?? 0).toLocaleString()}</span>
        ) : (
          <span className="tabular-nums text-gray-700">-</span>
        ),
    };
    const inboundBagCountCol = {
      title: t('wms.progress.bigBag.columns.inboundBagCount'),
      dataIndex: 'inboundBagCount',
      key: 'inboundBagCount',
      width: 120,
      render: (v: number) => <span className="tabular-nums">{Number(v ?? 0).toLocaleString()}</span>,
    };
    const statusCol = {
      title: t('wms.progress.bigBag.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: CustomerSummaryRow['status']) => (
        <Tag className="ml-1" color={s === 'high' ? 'red' : s === 'mid' ? 'orange' : 'green'}>
          {s === 'high' ? t('wms.progress.bigBag.status.high') : s === 'mid' ? t('wms.progress.bigBag.status.mid') : t('wms.progress.bigBag.status.normal')}
        </Tag>
      ),
    };

    if (showBagExpectedDiff) {
      // 大包差异 tab：客户名称 | 预期大包 | 入库大包 | 大包差异 | 大包差异率 | 状态
      const bagDiffCol = {
        title: t('wms.progress.bigBag.columns.bagDiff'),
        dataIndex: 'bagDiff',
        key: 'bagDiff',
        width: 120,
        render: (v: number) => {
          const n = Number(v ?? 0);
          const toneCls = n > 0 ? 'text-red-600 font-semibold' : n < 0 ? 'text-green-700 font-semibold' : '';
          return <span className={`inline tabular-nums ${toneCls}`}>{n.toLocaleString()}</span>;
        },
      };
      const bagDiffRateCol = {
        title: t('wms.progress.bigBag.columns.bagDiffRate'),
        dataIndex: 'bagDiffRate',
        key: 'bagDiffRate',
        width: 220,
        render: (rate: number) => <BigBagParcelDiffRateCell rate={Number(rate ?? 0)} />,
      };
      return [customerNameCol, expectedBagCountCol, inboundBagCountCol, bagDiffCol, bagDiffRateCol, statusCol];
    }

    // 包裹差异 tab：客户名称 | 入库大包数 | 预期包裹 | 入库包裹 | 差异包裹 | 差异包裹率 | 状态
    const parcelDiffCol = {
      title: t('wms.progress.bigBag.columns.parcelDiff'),
      dataIndex: 'parcelDiff',
      key: 'parcelDiff',
      width: 120,
      render: (v: number, record: CustomerSummaryRow) => {
        const n = Number(v ?? 0);
        const canDrill = n !== 0;
        const toneCls = n > 0 ? 'text-red-600 font-semibold' : n < 0 ? 'text-green-700 font-semibold' : '';
        if (!canDrill) return <span className={`inline tabular-nums ${toneCls}`}>{n}</span>;
        return (
          <span
            role="button"
            tabIndex={0}
            className={`inline tabular-nums ${toneCls} ${CLICKABLE_UNDERLINE}`}
            onClick={() =>
              openParcelDrilldown(
                {
                  customerId: record.customerId,
                  customerName: record.customerName,
                  bagType: null,
                  mawb: null,
                  bagNo: null,
                },
                BigBagDrilldownTitleSource.CustomerParcelDiffNotInbound
              )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openParcelDrilldown(
                  {
                    customerId: record.customerId,
                    customerName: record.customerName,
                    bagType: null,
                    mawb: null,
                    bagNo: null,
                  },
                  BigBagDrilldownTitleSource.CustomerParcelDiffNotInbound
                );
              }
            }}
          >
            {n}
          </span>
        );
      },
    };
    const parcelDiffRateCol = {
      title: t('wms.progress.bigBag.columns.parcelDiffRate'),
      dataIndex: 'parcelDiffRate',
      key: 'parcelDiffRate',
      width: 220,
      render: (rate: number) => <BigBagParcelDiffRateCell rate={Number(rate ?? 0)} />,
    };
    return [
      customerNameCol,
      inboundBagCountCol,
      { title: t('wms.progress.bigBag.columns.expectedParcelCount'), dataIndex: 'expectedParcelCount', key: 'expectedParcelCount', width: 120 },
      { title: t('wms.progress.bigBag.columns.inboundParcelCount'), dataIndex: 'inboundParcelCount', key: 'inboundParcelCount', width: 120 },
      parcelDiffCol,
      parcelDiffRateCol,
      statusCol,
    ];
  }, [openParcelDrilldown, showBagExpectedDiff, t]);


  // 处理时间范围变化
  const handleWorkProgressTimeRangeChange = (timeRange: { start: dayjs.Dayjs; end: dayjs.Dayjs }) => {
    setWorkProgressTimeRange(timeRange);
  };

  // 处理渠道变化
  const handleChannelChange = (channelId: string | null) => {
    setChannelId(channelId);
  };

  // 处理尾程商变化
  const handleCarrierChange = (carrierCode: number | null) => {
    setCarrierCode(carrierCode);
  };

  const items: TabsProps['items'] = useMemo(
    () => [
      {
        key: 'all',
        label: t('wms.progress.tabs.fullProgress'),
      },
      {
        key: 'big-bag',
        label: t('wms.progress.tabs.bigBagProgress'),
      },
    ],
    [t]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      {/* Tab页签 */}
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => setActiveTabKey(key as WorkProgressTabKey)}
        items={items}
        className="[&_.ant-tabs-tab]:font-bold"
      />

      {activeTabKey === 'all' ? (
        <>
          {/* 筛选条件 */}
          <WorkProgressFilters
            onTimeRangeChange={handleWorkProgressTimeRangeChange}
            onChannelChange={handleChannelChange}
            onCarrierChange={handleCarrierChange}
          />

          {/* 扫描包裹数折线图 */}
          <ScanningChart data={scanningData} loading={loading} statTypes={statTypes} />

          {/* 账号扫描数量表格 */}
          <AccountScanningTable data={accountScanningData} loading={loading} statTypes={statTypes} />
        </>
      ) : (
        <>
          {/* 大包进度下挂两个子切换：包裹差异 / 大包差异（紫色实心 Segmented，高度/padding 与下载按钮对齐） */}
          <style>{`
            .big-bag-diff-segmented.ant-segmented {
              padding: 2px;
            }
            .big-bag-diff-segmented.ant-segmented .ant-segmented-item {
              min-height: 28px;
            }
            .big-bag-diff-segmented.ant-segmented .ant-segmented-item-label {
              font-weight: 700;
              line-height: 28px;
              min-height: 28px;
              padding: 0 15px;
            }
            .big-bag-diff-segmented.ant-segmented .ant-segmented-item-selected {
              background: #7c3aed !important;
              color: #fff !important;
              box-shadow: none !important;
            }
            .big-bag-diff-segmented.ant-segmented .ant-segmented-item-selected .ant-segmented-item-label {
              color: #fff !important;
            }
            .big-bag-diff-segmented.ant-segmented .ant-segmented-thumb {
              background: #7c3aed !important;
              box-shadow: 0 1px 3px rgba(124, 58, 237, 0.45);
            }
          `}</style>
          <div className="mt-2 mb-3 flex items-center justify-between">
            <Segmented
              className="big-bag-diff-segmented"
              value={bigBagSubTabKey}
              onChange={(value) => {
                setBigBagSubTabKey(value as BigBagSubTabKey);
                setBigBagPage({ page: 1, pageSize: 10 });
                setExpandedCustomerKeys([]);
                setCustomerBreakdownById({});
              }}
              options={[
                { label: t('wms.progress.bigBag.subTabs.parcelDiff'), value: 'parcel' },
                { label: t('wms.progress.bigBag.subTabs.bagDiff'), value: 'bag' },
              ]}
            />
            <Button
              type="primary"
              loading={bigBagSubTabKey === 'bag' ? bagDiffMawbDownloadLoading : bagDetailDownloadLoading}
              disabled={!hubId || !bigBagFilters}
              onClick={handleBigBagDownload}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('wms.progress.bigBag.actions.download')}</span>
            </Button>
          </div>

          {/* 大包进度筛选条件（独立；切换子 tab 时通过 key 强制重挂载以重置内部状态） */}
          <BigBagProgressFilters
            key={`big-bag-filters-${bigBagSubTabKey}`}
            onChange={setBigBagFilters}
            customerOptions={bigBagFilterOptions.customers}
          />

          {/* 日期口径 + 扫描日期注解 + 下钻范围 + 数据更新时间，合并为一行黑色说明文字（分句用「；」） */}
          <p className="text-black mb-2">
            {t('wms.progress.bigBag.inboundDateBadge')}；
            {t('wms.progress.bigBag.bagScanDateNote')}；
            {t('wms.progress.bigBag.drilldownHint', { days: BIG_BAG_DRILLDOWN_HINT_DAYS })}
            {bigBagDataUpdatedAtText
              ? `；${t('wms.delivery.dailyTaskMonitoring.dataUpdatedAt')} ${bigBagDataUpdatedAtText}`
              : '。'}
          </p>

          {/* 指标卡（全量聚合，不受分页影响） */}
          {bigBagFilters && bigBagAggregate ? (
            <div className="mb-3">
              {(() => {
                const getTone = (rate: number) => {
                  const r = Number(rate ?? 0);
                  if (r <= 0) return { key: 'normal' as const, cls: 'text-emerald-600' };
                  if (r > 0.1) return { key: 'high' as const, cls: 'text-red-600' };
                  return { key: 'mid' as const, cls: 'text-orange-600' };
                };
                const parcelTone = getTone(Number(bigBagAggregate.parcelDiffRate ?? 0));
                const bagTone = getTone(Number(bigBagAggregate.bagDiffRate ?? 0));

                const parcelExpected = Number(bigBagAggregate.expectedParcelCountSum ?? 0);
                const parcelDiff = Number(bigBagAggregate.parcelDiffSum ?? 0);
                const bagExpected = Number(bigBagAggregate.expectedBagCountSum ?? 0);
                const bagDiff = Number(bigBagAggregate.bagDiffSum ?? 0);
                const bagInbound = Number(bigBagAggregate.inboundBagCountSum ?? 0);

                return (
                  <div className="grid gap-3 grid-cols-2">
                    {!showBagExpectedDiff ? (
                    <div className="rounded-lg shadow-sm border border-violet-200/70 p-4 bg-violet-50">
                      <div className="text-sm text-gray-700 font-semibold">{t('wms.progress.bigBag.kpi.parcelTotalDiffRateTitle')}</div>
                      <div className="mt-1 flex items-end gap-3">
                        <div className={`text-3xl font-extrabold tabular-nums ${parcelTone.cls}`}>
                          {(Number(bigBagAggregate.parcelDiffRate ?? 0) * 100).toFixed(2)}%
                        </div>
                      </div>
                      <div className="mt-2 text-sm font-semibold tabular-nums text-gray-700">
                        <span>{t('wms.progress.bigBag.kpi.diffPrefix')}</span>
                        {parcelDiff !== 0 ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className={`inline tabular-nums ${parcelTone.cls} ${CLICKABLE_UNDERLINE}`}
                            onClick={() => openBigBagKpiDrilldown('parcel', false)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openBigBagKpiDrilldown('parcel', false);
                              }
                            }}
                          >
                            {parcelDiff.toLocaleString()}
                          </span>
                        ) : (
                          <span className={`inline tabular-nums ${parcelTone.cls}`}>{parcelDiff.toLocaleString()}</span>
                        )}
                        <span>{t('wms.progress.bigBag.kpi.parcelDiffSummarySuffix', { expected: parcelExpected.toLocaleString() })}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {t('wms.progress.bigBag.kpi.inboundBagParcelDiffOnlyNote')}
                      </div>
                    </div>
                    ) : null}

                    {showBagExpectedDiff ? (
                    <div className="rounded-lg shadow-sm border border-violet-200/70 p-4 bg-violet-50">
                      <div className="text-sm text-gray-700 font-semibold">{t('wms.progress.bigBag.kpi.bagTotalDiffRateTitle')}</div>
                      <div className="mt-1 flex items-end gap-3">
                        <div className={`text-3xl font-extrabold tabular-nums ${bagTone.cls}`}>
                          {(Number(bigBagAggregate.bagDiffRate ?? 0) * 100).toFixed(2)}%
                        </div>
                      </div>
                      <div className="mt-2 text-sm font-semibold tabular-nums text-gray-700">
                        <span>{t('wms.progress.bigBag.kpi.diffPrefix')}</span>
                        {bagDiff !== 0 ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className={`inline tabular-nums ${bagTone.cls} ${CLICKABLE_UNDERLINE}`}
                            onClick={() => openBigBagKpiDrilldown('bag', false)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openBigBagKpiDrilldown('bag', false);
                              }
                            }}
                          >
                            {bagDiff.toLocaleString()}
                          </span>
                        ) : (
                          <span className={`inline tabular-nums ${bagTone.cls}`}>{bagDiff.toLocaleString()}</span>
                        )}
                        <span>{t('wms.progress.bigBag.kpi.bagInboundInfix')}</span>
                        {bagInbound !== 0 ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className={`inline tabular-nums text-gray-700 ${CLICKABLE_UNDERLINE}`}
                            onClick={() => openBigBagKpiDrilldown('bag', true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openBigBagKpiDrilldown('bag', true);
                              }
                            }}
                          >
                            {bagInbound.toLocaleString()}
                          </span>
                        ) : (
                          <span className="inline tabular-nums text-gray-700">{bagInbound.toLocaleString()}</span>
                        )}
                        <span>{t('wms.progress.bigBag.kpi.bagExpectedSuffix', { expected: bagExpected.toLocaleString() })}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{t('wms.progress.bigBag.kpi.bagDiffMawbOnlyNote')}</div>
                    </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          ) : null}

          {/* 大包进度表格（无折线图） */}
          <div ref={bigBagTableAnchorRef} />
          <Table<CustomerSummaryRow>
            rowKey="key"
            columns={customerColumns}
            dataSource={customerSummaryRows}
            loading={bigBagLoading}
            pagination={false}
            size="middle"
            /** 纵向可滚动区域内固定表头；视口近似扣除 Tab / 筛选 / KPI 等区域 */
            sticky
            scroll={{ x: bigBagNativeTableMinWidth - BIG_BAG_NATIVE_EXPAND_COL_PX, y: 'calc(100vh - 320px)' }}
            className="bg-white rounded-lg [&_.ant-table-expanded-row_.ant-table-cell]:!px-0 [&_.ant-table-expanded-row_.ant-table-cell]:!py-2 [&_.ant-table-row-expand-icon-cell]:!pl-0 [&_.ant-table-sticky-holder]:bg-[#fafafa]"
            expandable={{
              expandedRowRender: (record) => {
                const breakdown = customerBreakdownById[record.customerId] ?? null;
                const isExpanded = expandedCustomerKeys.includes(record.key);
                if (isExpanded && !breakdown && customerBreakdownLoading) {
                  return <div className="py-3 text-xs text-gray-400">{t('wms.progress.bigBag.messages.loading')}</div>;
                }

                /* ============================================================
                 * 大包差异 tab 的二级（mawb 行）+ 三级（mawb 下的大包行）渲染
                 * 与包裹差异 tab 结构不同：无 noMawb 段，列只有大包口径。
                 * ============================================================ */
                if (showBagExpectedDiff) {
                  const bagDiffBreakdown = breakdown as GetWorkProgressBagDiffCustomerBreakdownResponse['getWorkProgressBagDiffCustomerBreakdown'] | null;
                  const withMawbBagDiff: WorkProgressBagDiffCustomerMawbSummaryRow[] = bagDiffBreakdown?.withMawb ?? [];
                  if (withMawbBagDiff.length === 0) {
                    return <div className="py-3 text-xs text-gray-400">{t('wms.progress.bigBag.messages.noDetails')}</div>;
                  }

                  return (
                    <div className="space-y-3 py-1">
                      <div className="space-y-2">
                        <div className="pl-[48px] pt-1 text-xs font-medium text-gray-500">{t('wms.progress.bigBag.sections.withMawb')}</div>
                        <div className="ant-table ant-table-middle overflow-x-auto rounded-md border border-gray-100 bg-white">
                          <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed', minWidth: bigBagNativeTableMinWidth }}>
                            <colgroup>
                              <col style={{ width: BIG_BAG_NATIVE_EXPAND_COL_PX }} />
                              {bigBagNativeAlignedColPx.map((w, index) => (
                                <col key={`${index}-${w}`} style={{ width: w }} />
                              ))}
                            </colgroup>
                            <tbody>
                              {withMawbBagDiff.map((r) => {
                                const rowKey = r.mawb;
                                const isOpen = bagDiffMawbCache.isExpanded(record.customerId, rowKey);
                                const rr = Number(r.bagDiffRate ?? 0);
                                const tagColor = rr > 0.1 ? 'red' : rr > 0 ? 'orange' : 'green';
                                const tagLabel = rr > 0.1 ? t('wms.progress.bigBag.status.high') : rr > 0 ? t('wms.progress.bigBag.status.mid') : t('wms.progress.bigBag.status.normal');
                                const bagDiffNum = Number(r.bagDiffSum ?? 0);
                                const bagDiffTone = bagDiffNum > 0 ? 'text-red-600 font-semibold' : bagDiffNum < 0 ? 'text-green-700 font-semibold' : '';
                                return (
                                  <Fragment key={rowKey}>
                                    <tr className="ant-table-row transition-colors hover:bg-gray-50/90">
                                      <td className="ant-table-cell border-b border-gray-100" style={{ padding: '12px 8px', verticalAlign: 'middle', textAlign: 'center', width: '48px', boxSizing: 'border-box' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <button
                                            type="button"
                                            aria-expanded={isOpen}
                                            aria-label={String(isOpen ? t('wms.progress.bigBag.actions.collapseBagDetails') : t('wms.progress.bigBag.actions.expandBagDetails'))}
                                            onClick={() => void bagDiffMawbCache.toggle(record.customerId, rowKey, !isOpen)}
                                            className={
                                              isOpen
                                                ? 'ant-table-row-expand-icon ant-table-row-expand-icon-expanded'
                                                : 'ant-table-row-expand-icon ant-table-row-expand-icon-collapsed'
                                            }
                                            style={{ margin: '0', padding: '0' }}
                                          />
                                        </span>
                                      </td>
                                      <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3 align-middle">
                                        <div className="min-w-0 font-semibold text-violet-600 tabular-nums break-all leading-snug">{r.mawb}</div>
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.expectedBagCountSum ?? 0).toLocaleString()}
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.inboundBagCountSum ?? 0).toLocaleString()}
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums">
                                        <span className={`inline tabular-nums ${bagDiffTone}`}>{bagDiffNum.toLocaleString()}</span>
                                      </td>
                                      <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3 align-middle">
                                        <BigBagParcelDiffRateCell rate={rr} />
                                      </td>
                                      <td className="ant-table-cell whitespace-nowrap border-b border-gray-100 py-3 pl-4 pr-3 align-middle">
                                        <Tag color={tagColor}>{tagLabel}</Tag>
                                      </td>
                                    </tr>
                                    {isOpen ? (
                                      <tr className="ant-table-row">
                                        <td className="ant-table-cell !border-b-0 !p-0 bg-white" colSpan={bigBagExpandedColSpan}>
                                          <div className="pl-[48px] py-2 pr-3">
                                            <div className="overflow-hidden rounded-md bg-white">
                                              <MawbBagDiffSummaryTable
                                                customerId={record.customerId}
                                                customerName={record.customerName}
                                                mawb={r.mawb}
                                                rows={bagDiffMawbCache.rowsFor(record.customerId, r.mawb)}
                                                loading={bagDiffMawbCache.isLoading(record.customerId, r.mawb)}
                                                onDrilldown={openBagDetailDrilldown}
                                              />
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }

                /* 包裹差异 tab：保留原有 mawb + noMawb 全部包裹口径渲染 */
                const parcelBreakdown = breakdown as GetWorkProgressBagCustomerBreakdownResponse['getWorkProgressBagCustomerBreakdown'] | null;
                const withMawb: WorkProgressBagCustomerMawbSummaryRow[] = parcelBreakdown?.withMawb ?? [];
                const noMawb: WorkProgressBagCustomerNoMawbSummaryRow[] = parcelBreakdown?.noMawb ?? [];
                if (withMawb.length === 0 && noMawb.length === 0) {
                  return <div className="py-3 text-xs text-gray-400">{t('wms.progress.bigBag.messages.noDetails')}</div>;
                }

                const withMawbMerged = mergeWithMawbRowsByMawb(withMawb);

                return (
                  <div className="space-y-3 py-1">
                    {withMawb.length > 0 ? (
                      <div className="space-y-2">
                        <div className="pl-[48px] pt-1 text-xs font-medium text-gray-500">{t('wms.progress.bigBag.sections.withMawb')}</div>
                        {/**
                         * 不用嵌套 Ant Table：rc-table 展开列寬度与父表不完全一致，会导致二级数字列整行错位。
                         * 改用原生 table + colgroup，列宽与一级 Table scroll.x=980、客户～状态六列完全一致。
                         */}
                        <div className="ant-table ant-table-middle overflow-x-auto rounded-md border border-gray-100 bg-white">
                          <table
                            className="w-full border-collapse text-sm"
                            style={{ tableLayout: 'fixed', minWidth: bigBagNativeTableMinWidth }}
                          >
                            <colgroup>
                              <col style={{ width: BIG_BAG_NATIVE_EXPAND_COL_PX }} />
                              {bigBagNativeAlignedColPx.map((w, index) => (
                                <col key={`${index}-${w}`} style={{ width: w }} />
                              ))}
                            </colgroup>
                            <tbody>
                              {withMawbMerged.map((r) => {
                                const rowKey = r.mawb;
                                const isOpen = parcelDiffMawbCache.isExpanded(record.customerId, rowKey);
                                const rr = Number(r.parcelDiffRate ?? 0);
                                const tagColor = rr > 0.1 ? 'red' : rr > 0 ? 'orange' : 'green';
                                const tagLabel = rr > 0.1 ? t('wms.progress.bigBag.status.high') : rr > 0 ? t('wms.progress.bigBag.status.mid') : t('wms.progress.bigBag.status.normal');
                                return (
                                  <Fragment key={rowKey}>
                                    <tr className="ant-table-row transition-colors hover:bg-gray-50/90">
                                      <td className="ant-table-cell border-b border-gray-100" style={{ padding: '12px 8px', verticalAlign: 'middle', textAlign: 'center', width: '48px', boxSizing: 'border-box' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <button
                                            type="button"
                                            aria-expanded={isOpen}
                                            aria-label={String(isOpen ? t('wms.progress.bigBag.actions.collapseBagDetails') : t('wms.progress.bigBag.actions.expandBagDetails'))}
                                            onClick={() => void parcelDiffMawbCache.toggle(record.customerId, rowKey, !isOpen)}
                                            className={
                                              isOpen
                                                ? 'ant-table-row-expand-icon ant-table-row-expand-icon-expanded'
                                                : 'ant-table-row-expand-icon ant-table-row-expand-icon-collapsed'
                                            }
                                            style={{ margin: '0', padding: '0' }}
                                          />
                                        </span>
                                      </td>
                                      <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3 align-middle">
                                        <div className="min-w-0 font-semibold text-violet-600 tabular-nums break-all leading-snug">
                                          {r.mawb}
                                        </div>
                                      </td>
                                      {showBagExpectedDiff ? (
                                        <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                          {Number(r.expectedBagCountSum ?? 0) > 0 ? Number(r.expectedBagCountSum ?? 0).toLocaleString() : '-'}
                                        </td>
                                      ) : null}
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.inboundBagCountSum ?? 0).toLocaleString()}
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.expectedParcelCountSum ?? 0).toLocaleString()}
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.inboundParcelCountSum ?? 0).toLocaleString()}
                                      </td>
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 align-middle tabular-nums text-gray-700">
                                        {Number(r.parcelDiffSum ?? 0) !== 0 ? (
                                          <span
                                            role="button"
                                            tabIndex={0}
                                            className={CLICKABLE_UNDERLINE}
                                            onClick={() =>
                                              openParcelDrilldown(
                                                {
                                                  customerId: record.customerId,
                                                  customerName: record.customerName,
                                                  bagType: BIG_BAG_TYPE_WITH_MAWB,
                                                  mawb: r.mawb,
                                                  bagNo: null,
                                                },
                                                BigBagDrilldownTitleSource.MawbSummaryParcelDiffNotInbound
                                              )
                                            }
                                          >
                                            {Number(r.parcelDiffSum ?? 0).toLocaleString()}
                                          </span>
                                        ) : (
                                          Number(r.parcelDiffSum ?? 0).toLocaleString()
                                        )}
                                      </td>
                                      <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3 align-middle">
                                        <BigBagParcelDiffRateCell rate={rr} />
                                      </td>
                                      <td className="ant-table-cell whitespace-nowrap border-b border-gray-100 py-3 pl-4 pr-3 align-middle">
                                        <Tag color={tagColor}>{tagLabel}</Tag>
                                      </td>
                                    </tr>
                                    {isOpen ? (
                                      <tr className="ant-table-row">
                                        <td className="ant-table-cell !border-b-0 !p-0 bg-white" colSpan={bigBagExpandedColSpan}>
                                          <div className="pl-[48px] py-2 pr-3">
                                            <div className="overflow-hidden rounded-md bg-white">
                                              <MawbBagParcelSummaryTable
                                                customerId={record.customerId}
                                                customerName={record.customerName}
                                                mawb={r.mawb}
                                                rows={parcelDiffMawbCache.rowsFor(record.customerId, r.mawb)}
                                                loading={parcelDiffMawbCache.isLoading(record.customerId, r.mawb)}
                                                onBagDrilldown={openBagDetailDrilldown}
                                                onParcelDrilldown={openParcelDrilldown}
                                              />
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {noMawb.length > 0 ? (
                      <div className="space-y-2">
                        <div className="pl-[48px] pt-1 text-xs font-medium text-gray-500">{t('wms.progress.bigBag.sections.noMawb')}</div>
                        {/* 与「有提单号」二级同源生表布局：首列占位与展开列对齐，列宽与客户主表一致 */}
                        <div className="ant-table ant-table-middle overflow-x-auto rounded-md border border-gray-100 bg-white">
                          <table
                            className="w-full border-collapse text-sm"
                            style={{ tableLayout: 'fixed', minWidth: bigBagNativeTableMinWidth }}
                          >
                            <colgroup>
                              <col style={{ width: BIG_BAG_NATIVE_EXPAND_COL_PX }} />
                              {bigBagNativeAlignedColPx.map((w, index) => (
                                <col key={`${index}-${w}`} style={{ width: w }} />
                              ))}
                            </colgroup>
                            <tbody>
                              {noMawb.map((row) => {
                                const rk = `${row.bagNumber}-${row.arrivalDate}`;
                                const rr = Number(row.parcelDiffRate ?? 0);
                                const tagColor = rr > 0.1 ? 'red' : rr > 0 ? 'orange' : 'green';
                                const tagLabel = rr > 0.1 ? t('wms.progress.bigBag.status.high') : rr > 0 ? t('wms.progress.bigBag.status.mid') : t('wms.progress.bigBag.status.normal');
                                return (
                                  <tr key={rk} className="ant-table-row transition-colors hover:bg-gray-50/90">
                                    <td
                                      className="ant-table-cell border-b border-gray-100"
                                      style={{
                                        padding: '12px 8px',
                                        verticalAlign: 'middle',
                                        textAlign: 'center',
                                        width: `${BIG_BAG_NATIVE_EXPAND_COL_PX}px`,
                                        boxSizing: 'border-box',
                                      }}
                                      aria-hidden
                                    />
                                    <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3 align-top">
                                      <div className="min-w-0">
                                        <button
                                          type="button"
                                          className={`min-w-0 text-left font-semibold text-violet-600 tabular-nums break-all leading-snug ${CLICKABLE_UNDERLINE}`}
                                          onClick={() =>
                                            openBagDetailDrilldown(
                                              {
                                                customerId: record.customerId,
                                                customerName: record.customerName,
                                                bagType: BIG_BAG_TYPE_NO_MAWB,
                                                bigBagNo: row.bagNumber,
                                                detailArrivalDate: row.arrivalDate ?? null,
                                              },
                                              {
                                                titleSource: BigBagDrilldownTitleSource.NoMawbBagDetail,
                                                nextIsInbound: true,
                                              }
                                            )
                                          }
                                        >
                                          {row.bagNumber || '-'}
                                        </button>
                                        <div className="mt-0.5 flex items-center gap-x-3 whitespace-nowrap text-xs">
                                          <span className="text-gray-500 tabular-nums">{row.arrivalDate || '-'}</span>
                                        </div>
                                      </div>
                                    </td>
                                    {showBagExpectedDiff ? (
                                      <td className="ant-table-cell border-b border-gray-100 px-3 py-3 tabular-nums text-gray-700">-</td>
                                    ) : null}
                                    <td className="ant-table-cell border-b border-gray-100 px-3 py-3 tabular-nums text-gray-700">1</td>
                                    <td className="ant-table-cell border-b border-gray-100 px-3 py-3 tabular-nums text-gray-700">
                                      {Number(row.expectedParcelCountSum ?? 0).toLocaleString()}
                                    </td>
                                    <td className="ant-table-cell border-b border-gray-100 px-3 py-3 tabular-nums text-gray-700">
                                      {Number(row.inboundParcelCountSum ?? 0).toLocaleString()}
                                    </td>
                                    <td className="ant-table-cell border-b border-gray-100 px-3 py-3 tabular-nums text-gray-700">
                                      {Number(row.parcelDiffSum ?? 0) !== 0 ? (
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          className={CLICKABLE_UNDERLINE}
                                          onClick={() =>
                                            openParcelDrilldown(
                                              {
                                                customerId: record.customerId,
                                                customerName: record.customerName,
                                                bagType: BIG_BAG_TYPE_NO_MAWB,
                                                mawb: null,
                                                bagNo: row.bagNumber,
                                              },
                                              BigBagDrilldownTitleSource.NoMawbParcelDiffNotInbound
                                            )
                                          }
                                        >
                                          {Number(row.parcelDiffSum ?? 0).toLocaleString()}
                                        </span>
                                      ) : (
                                        Number(row.parcelDiffSum ?? 0).toLocaleString()
                                      )}
                                    </td>
                                    <td className="ant-table-cell min-w-0 border-b border-gray-100 px-3 py-3">
                                      <BigBagParcelDiffRateCell rate={rr} />
                                    </td>
                                    <td className="ant-table-cell whitespace-nowrap border-b border-gray-100 py-3 pl-4 pr-3">
                                      <Tag color={tagColor}>{tagLabel}</Tag>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              },
              expandedRowKeys: expandedCustomerKeys,
              onExpand: async (expanded, record) => {
                if (!hubId || !bigBagFilters) return;
                const nextKeys = expanded
                  ? Array.from(new Set([...expandedCustomerKeys, record.key]))
                  : expandedCustomerKeys.filter(k => k !== record.key);
                setExpandedCustomerKeys(nextKeys);
                if (!expanded) {
                  setCustomerBreakdownById((prev) => {
                    const n = { ...prev };
                    delete n[record.customerId];
                    return n;
                  });
                  parcelDiffMawbCache.clearForCustomer(record.customerId);
                  bagDiffMawbCache.clearForCustomer(record.customerId);
                  return;
                }
                const start = bigBagFilters.timeRange.start.startOf('day');
                const end = bigBagFilters.timeRange.end.startOf('day');
                if (!canOpenBigBagDrilldown(start, end)) return;
                if (bigBagSubTabKey === 'bag') {
                  const res = await fetchCustomerBreakdownBagDiff({
                    variables: {
                      filter: {
                        prePickupHubId: Number(hubId),
                        arrivalDateStart: start.format('YYYY-MM-DD'),
                        arrivalDateEnd: end.format('YYYY-MM-DD'),
                        customerId: record.customerId,
                      },
                    },
                  });
                  const data = res?.data?.getWorkProgressBagDiffCustomerBreakdown ?? null;
                  setCustomerBreakdownById(prev => ({ ...prev, [record.customerId]: data }));
                } else {
                  const res = await fetchCustomerBreakdownParcelDiff({
                    variables: {
                      filter: {
                        hubId: Number(hubId),
                        arrivalDateStart: start.format('YYYY-MM-DD'),
                        arrivalDateEnd: end.format('YYYY-MM-DD'),
                        customerId: record.customerId,
                        showBagExpectedDiff: false,
                      },
                    },
                  });
                  const data = res?.data?.getWorkProgressBagCustomerBreakdown ?? null;
                  setCustomerBreakdownById(prev => ({ ...prev, [record.customerId]: data }));
                }
              },
              rowExpandable: () => true,
            }}
          />

          <TablePaginationBar
            className="mt-4"
            total={bigBagTotal}
            current={bigBagPage.page}
            pageSize={bigBagPage.pageSize}
            onChange={(page, pageSize) => setBigBagPage({ page, pageSize })}
          />

          <BigBagProgressDrilldownDrawer
            open={bigBagDrilldown.open}
            context={bigBagDrilldown.context}
            mode={bigBagDrilldown.mode}
            isInbound={bigBagDrilldown.isInbound}
            titleSource={bigBagDrilldown.titleSource}
            onClose={() =>
              setBigBagDrilldown({
                open: false,
                context: null,
                mode: 'bag',
                isInbound: null,
                titleSource: null,
              })
            }
          />

          <WorkProgressBagDownloadModal
            visible={downloadModalVisible}
            onClose={() => setDownloadModalVisible(false)}
            fileName={downloadFileName}
            descriptionText={
              bigBagSubTabKey === 'bag'
                ? String(t('wms.progress.bigBag.messages.downloadBagReportDescription'))
                : undefined
            }
          />
        </>
      )}
    </div>
  );
};

export default WorkProgressPage;